import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import {
  Banner,
  Button,
  Card,
  ConfirmDialog,
  CopyField,
  DataTable,
  EmptyState,
  Grid,
  Input,
  Page,
  Panel,
  Stack,
  StatusBadge,
  Toolbar,
} from "@/components/ui";

import { spaceAddress } from "@/lib/env";

import { Paged } from "../components/Paged";
import { SpaceVerbs } from "../components/SpaceVerbs";
import { useConsole } from "../store";
import type { SpaceRow } from "../types";

import { useCreateSpace, useLoadedSpace, useSpaceActivities, useSpaceRows } from "./lib/queries";

/** Name a space and open its editor. The one control in the product that makes a space. */
function NewSpace() {
  const navigate = useNavigate();
  const create = useCreateSpace();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");

  const make = (): void => {
    if (create.isPending) return;
    create.mutate(name.trim(), {
      onSuccess: (made) => {
        setNaming(false);
        setName("");
        void navigate({
          to: "/console/spaces/$joinCode/control",
          params: { joinCode: made.join_code },
        });
      },
    });
  };

  if (!naming) {
    return (
      <Button
        onClick={() => {
          setNaming(true);
        }}
      >
        New space
      </Button>
    );
  }

  return (
    <Stack gap={2}>
      <Toolbar>
      <Input
        aria-label="Space name"
        placeholder="Name this space"
        value={name}
        autoFocus
        onChange={(event) => {
          setName(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") make();
          if (event.key === "Escape") setNaming(false);
        }}
      />
      <Button onClick={make} disabled={create.isPending}>
        {create.isPending ? "Making" : "Create"}
      </Button>
      <Button
        variant="ghost"
        onClick={() => {
          setNaming(false);
        }}
      >
        Cancel
      </Button>
      </Toolbar>
      {create.isError ? (
        <Banner tone="danger" aria-live="polite">
          {create.error.message}
        </Banner>
      ) : null}
    </Stack>
  );
}

function matchSpace(row: SpaceRow, needle: string): boolean {
  return `${row.space.name ?? ""} ${row.space.join_code}`.toLowerCase().includes(needle);
}

function SpaceCard({
  row,
  live,
  loadedId,
}: {
  readonly row: SpaceRow;
  readonly live: number;
  readonly loadedId: string | null;
}) {
  const navigate = useNavigate();
  const load = useConsole((state) => state.load);
  const unload = useConsole((state) => state.unload);
  const archive = useConsole((state) => state.archive);
  const [confirming, setConfirming] = useState(false);

  const spaceId = row.space.space_id;
  const joinCode = row.space.join_code;
  const isLoaded = loadedId === spaceId;

  return (
    <Card padding="none" aria-label={row.space.name ?? row.space.join_code}>
      <div className="cx-space-card">
        <div className="cx-space-card-top">
          <div className="cx-space-name">{row.space.name ?? row.space.join_code}</div>
          {live > 0 ? (
            <StatusBadge tone="success">{`${String(live)} running`}</StatusBadge>
          ) : (
            <StatusBadge tone="neutral">stopped</StatusBadge>
          )}
        </div>

        <CopyField label="Join" value={spaceAddress(joinCode)} />

        <Toolbar>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void navigate({ to: "/console/spaces/$joinCode/control", params: { joinCode } });
            }}
          >
            Open control
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (isLoaded) unload();
              else load(spaceId);
            }}
          >
            {isLoaded ? "Unload" : "Load"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void navigate({ to: "/console/spaces/$joinCode/canvases", params: { joinCode } });
            }}
          >
            Canvases
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={live > 0}
            onClick={() => {
              setConfirming(true);
            }}
          >
            {live > 0 ? "Running" : "Archive"}
          </Button>
        </Toolbar>
      </div>

      <ConfirmDialog
        open={confirming}
        title={`Archive ${row.space.name ?? joinCode}?`}
        description="Paired canvases go blank."
        confirmLabel="Archive"
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          archive(spaceId);
          setConfirming(false);
        }}
      />
    </Card>
  );
}

/** Spaces at the account level: the loaded one as a row, and the rest as a filtered page. */
export function SpacesList() {
  const [picking, setPicking] = useState(false);
  const load = useConsole((state) => state.load);
  const unload = useConsole((state) => state.unload);

  const restore = useConsole((state) => state.restore);
  const { rows, query } = useSpaceRows("all");
  const loaded = useLoadedSpace();
  const loadedId = loaded.data?.space_id ?? null;

  const liveOf = (spaceId: string): number =>
    (query.data?.spaces ?? []).find((one) => one.space.space_id === spaceId)
      ?.live_activity_count ?? 0;

  const active = rows.filter((one) => one.space.state !== "archived");
  const archived = rows.filter((one) => one.space.state === "archived");
  const loadedRow = rows.find((one) => one.space.space_id === loadedId) ?? null;
  const inLoaded = useSpaceActivities(loadedRow?.space.space_id ?? "");

  return (
    <Page title="Spaces" actions={<NewSpace />}>
      <Stack gap={6}>
        <Panel
          title="Loaded space"
          action={
            loadedRow === null ? null : (
              <StatusBadge tone="info">{loadedRow.space.name ?? loadedRow.space.join_code}</StatusBadge>
            )
          }
        >
          {loadedRow === null ? (
            <EmptyState bare headline="Nothing loaded" />
          ) : (
            <Stack gap={4}>
              <Toolbar>
                <SpaceVerbs spaceId={loadedRow.space.space_id} added={inLoaded.rows} />
                <Button size="sm" variant="ghost" onClick={unload}>
                  Unload
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={active.length === 0}
                  onClick={() => {
                    setPicking((held) => !held);
                  }}
                >
                  {picking ? "Done" : "Change"}
                </Button>
              </Toolbar>

              {picking ? (
                <Paged rows={active} pageSize={6} placeholder="Find a space" match={matchSpace}>
                  {(page) => (
                    <div role="listbox" aria-label="Loaded space">
                      {page.map((row) => (
                        <button
                          type="button"
                          key={row.space.space_id}
                          className="cx-loadctl-item"
                          role="option"
                          aria-selected={loadedId === row.space.space_id}
                          onClick={() => {
                            load(row.space.space_id);
                            setPicking(false);
                          }}
                        >
                          <span>{row.space.name ?? row.space.join_code}</span>
                          <span className="cx-code">{row.space.join_code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </Paged>
              ) : null}
            </Stack>
          )}
        </Panel>

        <Panel title="All spaces">
          {active.length > 0 ? (
            <Paged rows={active} pageSize={9} placeholder="Find a space" match={matchSpace}>
              {(page) => (
                <Grid minColumnWidth="320px" gap={5}>
                  {page.map((row) => (
                    <SpaceCard
                      key={row.space.space_id}
                      row={row}
                      live={liveOf(row.space.space_id)}
                      loadedId={loadedId}
                    />
                  ))}
                </Grid>
              )}
            </Paged>
          ) : (
            <EmptyState
              headline="No spaces yet"
              body="A space is where activities are mounted and every canvas is drawn."
              action={<NewSpace />}
            />
          )}
        </Panel>

        {archived.length > 0 ? (
          <Panel title="Archived">
            <Paged rows={archived} pageSize={10} placeholder="Find a space" match={matchSpace}>
              {(page) => (
                <DataTable
                  caption="Archived spaces"
                  columns={[
                    {
                      key: "name",
                      header: "Space",
                      emphasis: true,
                      render: (row: SpaceRow) => row.space.name ?? row.space.join_code,
                    },
                    {
                      key: "join_code",
                      header: "Join code",
                      mono: true,
                      render: (row: SpaceRow) => row.space.join_code,
                    },
                    {
                      key: "restore",
                      header: "",
                      render: (row: SpaceRow) => (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            restore(row.space.space_id);
                          }}
                        >
                          Restore
                        </Button>
                      ),
                    },
                  ]}
                  rows={page}
                />
              )}
            </Paged>
          </Panel>
        ) : null}
      </Stack>
    </Page>
  );
}
